import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch } from "../../../context/AuthContext";

/**
 * useOvertimeRequests
 *
 * Fetches overtime requests for a date range via the optimized
 * /attendance/overtime endpoint.
 */
const useOvertimeRequests = ({
  BASE_URL,
  language,
  viewType,
  getConfigForEmployee,
  overtimeApprovalRequired,
  isOpen = false,
}) => {
  const [overtimeRequests, setOvertimeRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  // Start with NO range so nothing is fetched on mount. This hook used to
  // eagerly load the whole current month — one whole-roster request PER DAY —
  // just to keep the overtime badge fresh. But the badge is derived from the
  // already-loaded page rows in index.jsx, not from here, so that month sweep
  // was pure waste. The drawer sets a real range (today, by default) when it
  // opens, and only then do we fetch.
  const [dateRange, setDateRange] = useState(null);

  // Track if we're in "show all" mode (dateRange === null)
  const [showAll, setShowAll] = useState(false);

  const lastFetchKey = useRef("");

  /**
   * Load overtime requests for the entire date range via a single backend query.
   */
  const loadOvertimeRequests = useCallback(async () => {
    const effectiveRange = showAll
      ? (() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth() - 6, 1)
          .toISOString()
          .split("T")[0];
        const end = now.toISOString().split("T")[0];
        return { start, end };
      })()
      : dateRange;

    if (!effectiveRange) return;

    const fetchKey = showAll ? "show_all" : `${effectiveRange.start}_${effectiveRange.end}`;
    if (lastFetchKey.current === fetchKey) return;
    lastFetchKey.current = fetchKey;

    setLoading(true);
    try {
      const url = new URL(`${BASE_URL}/attendance/overtime`);
      url.searchParams.append("startDate", effectiveRange.start);
      url.searchParams.append("endDate", effectiveRange.end);
      url.searchParams.append("status", "all");

      const resp = await apiFetch(url.toString(), {
        method: "GET",
        headers: {
          "Accept-Language": language,
          "x-view-type": viewType || "admin",
        },
      });

      if (!resp.ok) {
        throw new Error("Failed to fetch overtime requests");
      }

      const json = await resp.json();
      const records = Array.isArray(json?.data) ? json.data : [];

      const filtered = records.filter((r) => {
        if (!getConfigForEmployee && overtimeApprovalRequired === undefined) {
          return true;
        }
        const recordConfig = getConfigForEmployee?.(
          r.workingCompany,
          r.employeeType,
          r.date
        );
        return (
          (recordConfig?.overtimeApprovalRequired ??
            overtimeApprovalRequired ?? true) === true
        );
      });

      setOvertimeRequests(filtered);
    } catch (err) {
      console.error("Error loading overtime requests:", err);
      setOvertimeRequests([]);
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, dateRange, showAll, getConfigForEmployee, language, overtimeApprovalRequired, viewType]);

  /**
   * Force re-fetch (e.g. after an approval action).
   */
  const refreshOvertimeRequests = useCallback(() => {
    lastFetchKey.current = "";
    loadOvertimeRequests();
  }, [loadOvertimeRequests]);

  // Update a single request locally (e.g. after approval/rejection)
  const updateLocalRequest = useCallback((id, updates) => {
    setOvertimeRequests((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...updates } : r))
    );
  }, []);

  // Custom setter that handles null ("show all" mode)
  const handleSetDateRange = useCallback((newRange) => {
    if (newRange === null) {
      setShowAll(true);
      // Keep the last dateRange for reference when switching back
    } else {
      setShowAll(false);
      setDateRange(newRange);
    }
  }, []);

  // Re-fetch when date range or showAll changes
  useEffect(() => {
    if (showAll || (dateRange && dateRange.start && dateRange.end)) {
      lastFetchKey.current = ""; // reset cache so it re-fetches
      loadOvertimeRequests();
    }
  }, [dateRange?.start, dateRange?.end, showAll]); // eslint-disable-line react-hooks/exhaustive-deps

  // Always fetch fresh data every time the drawer OPENS — even if the date
  // range is unchanged (e.g. reopening on the same day). The date-range effect
  // above only fires when start/end actually change, so reopening with the same
  // range would otherwise show stale requests. Busting the cache key guarantees
  // loadOvertimeRequests actually hits the network rather than short-circuiting.
  useEffect(() => {
    if (isOpen && (showAll || (dateRange && dateRange.start && dateRange.end))) {
      lastFetchKey.current = "";
      loadOvertimeRequests();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    overtimeRequests,
    overtimeLoading: loading,
    overtimeDateRange: showAll ? null : dateRange,
    setOvertimeDateRange: handleSetDateRange,
    loadOvertimeRequests,
    refreshOvertimeRequests,
    updateLocalRequest,
  };
};

export default useOvertimeRequests;
